import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const INVITE_TEACHER_URL = process.env.REACT_APP_INVITE_TEACHER_URL;

export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// ── AUTH ─────────────────────────────────────────────────────

export async function signInWithGoogle() {
  const { error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: window.location.origin + '/admin' }
  });
  if (error) throw error;
}

export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function getSession() {
  const { data } = await supabase.auth.getSession();
  return data.session;
}

// Look up the parent/teacher record for a signed-in account.
// Never creates one: an account we don't recognise gets no access rather than
// a new row. (This is where "everyone we don't know is a teacher" died.)
export async function getUserRecord(session) {
  if (!session) return null;
  const { user } = session;

  // First try by ID (Google OAuth users)
  const { data: existingById } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (existingById) {
    // Only update name if the provider actually supplied one AND it has changed.
    // Never fall back to user.email — password users have no full_name, and the
    // fallback used to overwrite their stored name with their email on every login.
    const incomingName = user.user_metadata?.full_name;
    if (incomingName && existingById.name !== incomingName) {
      const { data } = await supabase
        .from('users')
        .update({ name: incomingName })
        .eq('id', user.id)
        .select()
        .single();
      return data || existingById;
    }
    return existingById;
  }

  // Fallback — look up by email (invite flow users)
  const { data: existingByEmail } = await supabase
    .from('users')
    .select('*')
    .eq('email', user.email)
    .maybeSingle();

  if (existingByEmail) {
    // Sync the auth ID — never overwrite role.
    // Same rule as above: only write name when the provider supplied one.
    const incomingName = user.user_metadata?.full_name;
    const updates = { id: user.id };
    if (incomingName && existingByEmail.name !== incomingName) {
      updates.name = incomingName;
    }
    const { data } = await supabase
      .from('users')
      .update(updates)
      .eq('email', user.email)
      .select()
      .single();
    return data || existingByEmail;
  }

  return null;
}

// Mark password as set for invited teachers
export async function setPasswordComplete(userId) {
  const { error } = await supabase
    .from('users')
    .update({ password_set: true })
    .eq('id', userId);
  if (error) throw error;
}

// ── STUDENT SIGN-IN RECOGNITION ──────────────────────────────

// The student row already linked to this login account, or null.
export async function getStudentByAuthId(authUserId) {
  const { data, error } = await supabase
    .from('students')
    .select('*')
    .eq('auth_user_id', authUserId)
    .maybeSingle();
  if (error) { console.error('getStudentByAuthId:', error); return null; }
  return data || null;
}

// A student row pre-registered to this email, whatever its link state.
// Read-only — the caller decides, and the linking itself happens server-side.
export async function getStudentByEmail(email) {
  if (!email) return null;
  const { data, error } = await supabase
    .from('students')
    .select('*')
    .eq('email', email.toLowerCase())
    .maybeSingle();
  if (error) { console.error('getStudentByEmail:', error); return null; }
  return data || null;
}

// First Google sign-in for a pre-registered student: links this account to
// their row. Every fail-safe lives in the SQL function, not here — it writes
// auth_user_id and nothing else, only on a row whose pre-registered email
// matches the signed-in Google address, and only while that row is unclaimed.
// Returns the student row on success, null on refusal.
export async function claimStudentAccount() {
  const { data, error } = await supabase.rpc('claim_student_account');
  if (error) { console.error('claimStudentAccount:', error); return null; }
  return data || null;
}

// Clears the forced-password-change flag on the caller's own student row.
export async function markStudentPasswordSet() {
  const { data, error } = await supabase.rpc('student_password_set_complete');
  if (error) throw error;
  return data || null;
}

// Works out who a signed-in account belongs to. The order is the security:
// students are recognised before anything else (they are never users-table
// people), and an account recognised by nothing gets nothing — no row is
// created anywhere, for anyone.
// Returns { kind: 'student' | 'parent' | 'teacher' | 'none', student?, user?, reason? }
export async function resolveSession(session) {
  if (!session?.user) return { kind: 'none', reason: 'no-session' };
  const { user } = session;
  const email = (user.email || '').toLowerCase();

  // 1. Account already linked to a student row.
  const linked = await getStudentByAuthId(user.id);
  if (linked) return { kind: 'student', student: linked };

  // 2. Pre-registered student signing in for the first time.
  if (email) {
    const preRegistered = await getStudentByEmail(email);
    if (preRegistered) {
      // Already claimed by a different account (step 1 would have caught our
      // own). Never relink — a mistyped pre-registration must fail safe.
      if (preRegistered.auth_user_id) return { kind: 'none', reason: 'mismatch' };
      const claimed = await claimStudentAccount();
      if (claimed?.auth_user_id === user.id) return { kind: 'student', student: claimed };
      return { kind: 'none', reason: 'claim-refused' };
    }
  }

  // 3. Parent or teacher, as before.
  const dbUser = await getUserRecord(session);
  if (dbUser?.role === 'parent') return { kind: 'parent', user: dbUser };
  if (dbUser) return { kind: 'teacher', user: dbUser };

  // 4. Recognised by nothing.
  return { kind: 'none', reason: 'unknown' };
}

// ── STUDENT ──────────────────────────────────────────────────

export async function hashPin(pin) {
  const enc = new TextEncoder();
  const buf = await crypto.subtle.digest('SHA-256', enc.encode(pin + 'hw-underground-salt'));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

export async function verifyPin(pin, hash) {
  return await hashPin(pin) === hash;
}

// Returns the first student linked to a parent — used for single-student parent flows
export async function getStudentByParent(parentId) {
  const { data, error } = await supabase
    .from('student_parents')
    .select('students(*)')
    .eq('parent_id', parentId)
    .limit(1)
    .single();
  if (error) return null;
  return data?.students || null;
}

// Creates a new student and links them to the given parent via student_parents
export async function createStudent(name, pin, parentId) {
  const pin_hash = await hashPin(pin);
  // access_token is NOT NULL/UNIQUE on students but inert since Stage E — nothing reads it.
  const access_token = crypto.randomUUID();
  const { data, error } = await supabase
    .from('students')
    .insert({ name, pin_hash, access_token })
    .select()
    .single();
  if (error) throw error;

  const { error: linkError } = await supabase
    .from('student_parents')
    .insert({ student_id: data.id, parent_id: parentId });
  if (linkError) throw linkError;

  return data;
}

export async function updateStudentPin(studentId, newPin) {
  const pin_hash = await hashPin(newPin);
  const { error } = await supabase
    .from('students')
    .update({ pin_hash })
    .eq('id', studentId);
  if (error) throw error;
}

// ── PARENT ACCESS ─────────────────────────────────────────────

// Returns all students linked to a parent
export async function getStudentsByParent(parentId) {
  const { data, error } = await supabase
    .from('student_parents')
    .select('students(*)')
    .eq('parent_id', parentId)
    .order('created_at', { ascending: true });
  if (error) return [];
  return (data || []).map(r => r.students).filter(Boolean);
}

// Returns all parents linked to a student
export async function getParentsByStudent(studentId) {
  const { data, error } = await supabase
    .from('student_parents')
    .select('users(*)')
    .eq('student_id', studentId);
  if (error) return [];
  return (data || []).map(r => r.users).filter(Boolean);
}

// Links an existing parent user to a student
export async function addParentToStudent(studentId, parentId) {
  const { error } = await supabase
    .from('student_parents')
    .insert({ student_id: studentId, parent_id: parentId });
  if (error) throw error;
}

// Removes a parent's access to a student
export async function removeParentFromStudent(studentId, parentId) {
  const { error } = await supabase
    .from('student_parents')
    .delete()
    .eq('student_id', studentId)
    .eq('parent_id', parentId);
  if (error) throw error;
}

// ── ASSIGNMENTS ──────────────────────────────────────────────

export async function getAssignments(studentId) {
  const { data, error } = await supabase
    .from('assignments')
    .select(`*, steps(*)`)
    .eq('student_id', studentId)
    .order('due_date', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function createAssignment(studentId, assignment, steps, addedBy, isStudent) {
  const insertData = {
    student_id: studentId,
    name: assignment.name,
    subject: assignment.subject,
    due_date: assignment.due_date,
    color: assignment.color,
    time_estimate: assignment.time_estimate || null,
    caution_flag: false,
  };
  if (isStudent) {
    insertData.added_by_student = addedBy;
  } else {
    insertData.added_by = addedBy;
  }
  const { data, error } = await supabase
    .from('assignments')
    .insert(insertData)
    .select()
    .single();
  if (error) throw error;

  if (steps && steps.length > 0) {
    const stepRows = steps.map((s, i) => ({
      assignment_id: data.id,
      label: s.label,
      position: i,
      done: false
    }));
    const { error: stepsError } = await supabase.from('steps').insert(stepRows);
    if (stepsError) throw stepsError;
  }
  return data;
}

export async function updateAssignment(id, updates) {
  const { error } = await supabase
    .from('assignments')
    .update(updates)
    .eq('id', id);
  if (error) throw error;
}

export async function deleteAssignment(id) {
  const { error } = await supabase
    .from('assignments')
    .delete()
    .eq('id', id);
  if (error) throw error;
}

// ── STEPS ────────────────────────────────────────────────────

export async function toggleStep(stepId, done) {
  const { error } = await supabase
    .from('steps')
    .update({ done, done_at: done ? new Date().toISOString() : null })
    .eq('id', stepId);
  if (error) throw error;
}

export async function replaceSteps(assignmentId, steps) {
  await supabase.from('steps').delete().eq('assignment_id', assignmentId);
  if (steps.length === 0) return;
  const rows = steps.map((s, i) => ({
    assignment_id: assignmentId,
    label: s.label,
    position: i,
    done: s.done || false
  }));
  const { error } = await supabase.from('steps').insert(rows);
  if (error) throw error;
}

// ── TEACHER ACCESS ───────────────────────────────────────────

export async function getTeachers(studentId) {
  const { data, error } = await supabase
    .from('teacher_access')
    .select('*, users!teacher_id(*)')
    .eq('student_id', studentId);
  if (error) throw error;
  return data || [];
}

export async function inviteTeacher(email, studentId, grantedBy) {
  const response = await fetch(INVITE_TEACHER_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, studentId, grantedBy })
  });

  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Invite failed');
  return result;
}

export async function removeTeacher(teacherId, studentId) {
  const { error } = await supabase
    .from('teacher_access')
    .delete()
    .eq('teacher_id', teacherId)
    .eq('student_id', studentId);
  if (error) throw error;
}

// ── ACTIVITY LOG ─────────────────────────────────────────────

export async function logActivity(studentId, eventType, description) {
  try {
    const { error } = await supabase.from('activity_log').insert({
      student_id: studentId,
      event_type: eventType,
      description
    });
    if (error) console.error('logActivity:', error);
  } catch (e) { console.error('logActivity:', e); }
}

export async function getActivityLog(studentId, limit = 14) {
  const { data, error } = await supabase
    .from('activity_log')
    .select('*')
    .eq('student_id', studentId)
    .order('created_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}

// ── TEACHER SIGN-OFF ─────────────────────────────────────────

export async function createSignoff(studentId, teacherId) {
  const { data, error } = await supabase
    .from('teacher_signoffs')
    .insert({ student_id: studentId, teacher_id: teacherId })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function removeSignoff(signoffId) {
  const { error } = await supabase
    .from('teacher_signoffs')
    .delete()
    .eq('id', signoffId);
  if (error) throw error;
}

// Fixed: was using .single() which throws 406 when no rows exist
export async function getLatestSignoff(studentId) {
  const { data, error } = await supabase
    .from('teacher_signoffs')
    .select('*, users(name, email)')
    .eq('student_id', studentId)
    .order('signed_off_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return null;
  return data;
}

export async function getSignoffHistory(studentId, limit = 7) {
  const { data, error } = await supabase
    .from('teacher_signoffs')
    .select('*, users(name, email)')
    .eq('student_id', studentId)
    .order('signed_off_at', { ascending: false })
    .limit(limit);
  if (error) return [];
  return data || [];
}

// ── SIGN-OFF EMAIL SETTINGS ──────────────────────────────────
// One signoff_settings row per student, read and written directly under the
// parent policies (select / insert / update; no delete). Teachers see no rows.

export async function getSignoffSettings(studentId) {
  const { data, error } = await supabase
    .from('signoff_settings')
    .select('*')
    .eq('student_id', studentId)
    .maybeSingle();
  if (error) throw error;
  return data;
}

// Inserts the row on a student's first save; afterwards updates only the
// columns in `patch`.
export async function upsertSignoffSettings(studentId, patch) {
  const { data, error } = await supabase
    .from('signoff_settings')
    .upsert(
      { ...patch, student_id: studentId, updated_at: new Date().toISOString() },
      { onConflict: 'student_id' }
    )
    .select()
    .single();
  if (error) throw error;
  return data;
}

// ── STUDENTS (multi-student / multi-teacher) ──────────────────

export async function getStudentsByTeacher(teacherId) {
  const { data, error } = await supabase
    .from('teacher_access')
    .select('*, students(*)')
    .eq('teacher_id', teacherId);
  if (error) return [];
  return (data || []).map(r => r.students).filter(Boolean);
}
